import React from 'react';
import PropTypes from 'prop-types';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';

import { useStyles } from '~/assets/styles';
import { useClaim } from '~/components/ClaimContainer';
import { getContact } from '~/components/Contact';
import { PERMISSION_ACTIONS, PERMISSION_VERBS } from '~/components/core';
import Chip from '~/components/core/Atomic/Chip/Chip';
import Tooltip from '~/components/core/Atomic/Tooltip';
import { useCms } from '~/components/hooks/useCms';
import { useHasPermission } from '~/components/hooks/useHasPermission';
import { InvolvedPersonInfoTitle } from '~/components/InvolvedPersonHoverInfo';
import InvolvedPropertyInfoTitle from '~/components/InvolvedPropertyHoverInfo';
import OverflowTextWithToolTip from '~/components/OverflowTextWithToolTip';
import { isDatesDiffLessThan18Years } from '~/DateTimeUtils';

function ExposureDetailedTitle({
  exposure,
  separator,
  withInfoIcon = true,
  withDescOverflow = false,
  withMinorChip = true,
}) {
  const { claim } = useClaim();
  const { classes } = useStyles();
  const involvedPerson = exposure.involved_person;
  const involvedProperty = exposure.involved_property;

  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap' }}>
      {withDescOverflow ? (
        <OverflowTextWithToolTip maxWidth="150px">{exposure.coverage_type_desc}</OverflowTextWithToolTip>
      ) : (
        exposure.coverage_type_desc
      )}
      {involvedPerson && (
        <>
          {separator ? separator : <>&nbsp;-&nbsp;</>}
          <InvolvedPersonInfoTitle
            claimId={exposure.claim_id}
            involvedPerson={involvedPerson}
            exposureId={exposure.id}
            withInfoIcon={withInfoIcon}
          />
          {withMinorChip && <MinorChip contactId={involvedPerson.contact_id} />}
        </>
      )}
      {involvedProperty && (
        <>
          {separator ? separator : <>&nbsp;-&nbsp;</>}
          <InvolvedPropertyInfoTitle
            claimId={exposure.claim_id}
            involvedProperty={involvedProperty}
            withInfoIcon={withInfoIcon}
          />
          {withMinorChip && <MinorChip contactId={involvedProperty.owner_contact_id} />}
        </>
      )}
      {exposure.is_medicare_eligible === 'Positive' && (
        <span style={{ paddingLeft: '10px' }}>
          <Tooltip title="Medicare Eligibility Confirmed">
            <VerifiedUserIcon color="primary" />
          </Tooltip>
        </span>
      )}
      {exposure.coverage_type === 'coverage_wc_compensation' && claim?.compensation_type && (
        <Chip size="small" color="primary" label={claim.compensation_type} className={classes.chip} />
      )}
    </div>
  );
}

ExposureDetailedTitle.propTypes = {
  exposure: PropTypes.object.isRequired,
  separator: PropTypes.node,
  withInfoIcon: PropTypes.bool,
  withDescOverflow: PropTypes.bool,
  withMinorChip: PropTypes.bool,
};

function MinorChip({ contactId }) {
  const { claim } = useClaim();
  const { userOrganization } = useCms();
  const { classes } = useStyles();
  const [contact, setContact] = React.useState();
  const claimDateOfLoss = claim && claim.incident && claim.incident.date_of_loss;
  const hasContactReadPermission = useHasPermission({
    action: PERMISSION_ACTIONS.CONTACT,
    verb: PERMISSION_VERBS.READ,
  });

  React.useEffect(() => {
    async function fetchContact() {
      const contact = await getContact(contactId, null, userOrganization.id);
      setContact(contact);
    }
    // Without CONTACT:READ the fetch 403s on every exposure title; unlike the ContactEntity
    // fetches it has no catch, so it fails as an unhandled rejection rather than the global
    // permission dialog. The chip simply stays hidden for restricted users.
    if (contactId && hasContactReadPermission) {
      fetchContact();
    }
  }, [contactId, userOrganization.id, hasContactReadPermission]);

  if (contact && contact.date_of_birth && isDatesDiffLessThan18Years(contact.date_of_birth, claimDateOfLoss)) {
    return <Chip size="small" color="primary" label="Minor" className={`${classes.chip}`} />;
  }

  return null;
}

MinorChip.propTypes = {
  contactId: PropTypes.any,
};

export { ExposureDetailedTitle };
